/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {AlertNotificationSeverity} from '../../alert-notification-severity.model';
import {Subject, Subscription} from 'rxjs';

@Component({
    selector: 'app-generic-modal',
    templateUrl: './generic-modal.component.html',
    styleUrls: ['./generic-modal.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
/**
 * <p>This is a generic confirmation modal, to be used on add/edit scenarios, such as the business settings add/edit actions.
 * <p><i>Note: it should use the resulting modals from {@link https://atc.bmwgroup.net/jira/browse/CTWILS-2360|CTWILS-2360}
 * or it should be used as the basis for them.</i>
 */
export class GenericModalComponent implements OnInit, OnDestroy {

    @Input() title = 'Confirmation';
    @Input() primaryActionTitle = 'Ok';
    @Input() secondaryActionTitle = 'Cancel';
    @Input() primaryActionEnabled = true;
    @Input() secondaryActionEnabled = true;
    @Input() alertNotificationSeverity: AlertNotificationSeverity = AlertNotificationSeverity.INFORMATION;

    @Input() isLoading: Subject<boolean>;

    @Output() primaryActionEvent = new EventEmitter<void>();
    @Output() secondaryActionEvent = new EventEmitter<void>();
    @Output() closeEvent = new EventEmitter<void>();

    public loading = false;

    private subscriptions = new Subscription();

    constructor(public activeModal: NgbActiveModal) {
    }

    get headerClass(): string {
        let finalClass = 'generic-modal-header';
        switch (this.alertNotificationSeverity) {
            case AlertNotificationSeverity.INFORMATION:
                finalClass = finalClass + ' information';
                break;
            case AlertNotificationSeverity.ERROR:
                finalClass = finalClass + ' error';
                break;
            case AlertNotificationSeverity.WARNING:
                finalClass = finalClass + ' warning';
                break;
            case AlertNotificationSeverity.SUCCESS:
                finalClass = finalClass + ' success';
                break;
            default:
                finalClass = finalClass + ' information';
        }
        return finalClass;
    }

    get primaryButtonClass(): string {
        let buttonClass = 'btn-primary';
        if (this.alertNotificationSeverity === AlertNotificationSeverity.ERROR) {
            buttonClass = 'btn-primary-red';
        }
        return buttonClass;
    }

    get disableSecondaryButton(): boolean {
        return !this.secondaryActionEnabled || this.loading;
    }

    get disablePrimaryButton(): boolean {
        return !this.primaryActionEnabled || this.loading;
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    ngOnInit(): void {
        if (this.isLoading != null) {
            this.subscriptions.add(
                this.isLoading.subscribe((loading) => {
                    setTimeout(() => {
                        this.loading = loading;
                    });
                }));
        }
    }

    clickPrimaryButton(): void {
        // this.loading = true;
        this.primaryActionEvent.emit();
        this.activeModal.dismiss();
    }

    clickSecondaryButton(): void {
        this.secondaryActionEvent.emit();
        this.activeModal.dismiss();
    }

    clickCloseButton(): void {
        this.closeEvent.emit();
        this.activeModal.dismiss();
    }
}
